/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * metrics.ts — metrics (services/operator-web-app).
 */

/**
 * E7-S1: Frontend observability metrics.
 * E8-S2: Hint adoption counters for pilot KPI (hints received, used, irrelevant).
 * Dashboard can read via getMetrics(); optional reporter for backend.
 */

const MAX_SAMPLES = 100;

export interface MetricsSnapshot {
  eventLagMs: number[];
  eventLagByType: Record<string, number[]>;
  hintLatencyMs: number[];
  reconnectTotal: number;
  presenceTimeoutIncidentTotal: number;
  /** E8-S2: Pilot — hint adoption. */
  hintReceivedTotal: number;
  hintMarkedUsedTotal: number;
  hintMarkedIrrelevantTotal: number;
  webVitals: {
    cls?: number;
    fcp?: number;
    inp?: number;
    lcp?: number;
    ttfb?: number;
  };
}

type Reporter = (name: string, value: number, tags?: Record<string, string>) => void;

class MetricsCollector {
  private eventLagMs: number[] = [];
  private eventLagByType: Record<string, number[]> = {};
  private hintLatencyMs: number[] = [];
  private reconnectTotal = 0;
  private presenceTimeoutIncidentTotal = 0;
  private hintReceivedTotal = 0;
  private hintMarkedUsedTotal = 0;
  private hintMarkedIrrelevantTotal = 0;
  private webVitals: MetricsSnapshot['webVitals'] = {};
  private reporter: Reporter | null = null;

  reportEventLag(eventType: string, lagMs: number): void {
    this.pushSample(this.eventLagMs, lagMs);
    if (!this.eventLagByType[eventType]) this.eventLagByType[eventType] = [];
    this.pushSample(this.eventLagByType[eventType], lagMs);
    this.reporter?.('event_lag_ms', lagMs, { event_type: eventType });
  }

  reportHintLatency(latencyMs: number): void {
    this.pushSample(this.hintLatencyMs, latencyMs);
    this.reporter?.('hint_latency_ms', latencyMs, {});
  }

  reportReconnect(): void {
    this.reconnectTotal += 1;
    this.reporter?.('reconnect_total', this.reconnectTotal, {});
  }

  reportPresenceTimeoutIncident(): void {
    this.presenceTimeoutIncidentTotal += 1;
    this.reporter?.('presence_timeout_incident_total', this.presenceTimeoutIncidentTotal, {});
  }

  /** E8-S2: Pilot — count hints received (hint_generated applied). */
  reportHintReceived(): void {
    this.hintReceivedTotal += 1;
    this.reporter?.('hint_received_total', this.hintReceivedTotal, {});
  }

  reportHintMarkedUsed(): void {
    this.hintMarkedUsedTotal += 1;
    this.reporter?.('hint_marked_used_total', this.hintMarkedUsedTotal, {});
  }

  reportHintMarkedIrrelevant(): void {
    this.hintMarkedIrrelevantTotal += 1;
    this.reporter?.('hint_marked_irrelevant_total', this.hintMarkedIrrelevantTotal, {});
  }

  reportWebVital(name: 'cls' | 'fcp' | 'inp' | 'lcp' | 'ttfb', value: number): void {
    this.webVitals[name] = value;
    this.reporter?.(`web_vital_${name}`, value, {});
  }

  setReporter(fn: Reporter | null): void {
    this.reporter = fn;
  }

  getMetrics(): MetricsSnapshot {
    return {
      eventLagMs: [...this.eventLagMs],
      eventLagByType: { ...this.eventLagByType },
      hintLatencyMs: [...this.hintLatencyMs],
      reconnectTotal: this.reconnectTotal,
      presenceTimeoutIncidentTotal: this.presenceTimeoutIncidentTotal,
      hintReceivedTotal: this.hintReceivedTotal,
      hintMarkedUsedTotal: this.hintMarkedUsedTotal,
      hintMarkedIrrelevantTotal: this.hintMarkedIrrelevantTotal,
      webVitals: { ...this.webVitals },
    };
  }

  /** Only for tests: reset all counters and samples. */
  __resetForTesting(): void {
    this.eventLagMs = [];
    this.eventLagByType = {};
    this.hintLatencyMs = [];
    this.reconnectTotal = 0;
    this.presenceTimeoutIncidentTotal = 0;
    this.hintReceivedTotal = 0;
    this.hintMarkedUsedTotal = 0;
    this.hintMarkedIrrelevantTotal = 0;
    this.webVitals = {};
  }

  private pushSample(arr: number[], value: number): void {
    arr.push(value);
    if (arr.length > MAX_SAMPLES) arr.shift();
  }
}

let instance: MetricsCollector | null = null;

export function getMetricsCollector(): MetricsCollector {
  if (!instance) instance = new MetricsCollector();
  return instance;
}

export function getMetrics(): MetricsSnapshot {
  return getMetricsCollector().getMetrics();
}

/** p50 / p95 helpers for dashboard */
export function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  const i = Math.max(0, idx);
  return sorted[i];
}

export function sortedCopy(arr: number[]): number[] {
  return [...arr].sort((a, b) => a - b);
}
